const Map = require('lodash/map');

const PrefsDomain = require('../../domain/preferences');
const AuthConstants = require('./constants');
const Debug = require('./debug');
const AuthPersistence = require('./persistence');
const RoutingTable = require('./routing-table');
const Policy = require('./policy');

const internals = {};

module.exports = {
    getSignupPrefillEmail,
    shouldResetRememberedEmailContext,
    buildRememberedEmailUpdate,
    buildLoginContextReset,
    buildSignupContextReset,
    mergeSignupProgressContext,
    signupContextReducer,
    signupIdentifyReducer,
    signupAuthenticateReducer,
    signupProfileReducer,
    shouldClearSignupStateForAction,
    isInitPrerequisiteSatisfied,
    isSignupAuthenticationPrerequisiteSatisfied,
    isSignupPasswordPrerequisiteSatisfied,
    isSignupSSOPrerequisiteSatisfied,
    getSignupContext,
    getSignupIdentifyDetails,
    getSignupAuthenticateDetails,
    getSignupProfileDetails,
    getSignupDetailsForCompletion,
    getSignupCompletionInterests,
    getSignupCompletionPassionInterests,
    getSignupCompletionRole,
    getSignupCompletionRolePermissions,
    stripNulls
};

function getSignupPrefillEmail({
    identifyContext = {},
    signupContext = {},
    loginContext = {}
} = {}) {

    return Policy.normalizeEmail(identifyContext.email)
        || Policy.normalizeEmail(signupContext.email)
        || Policy.normalizeEmail(loginContext.email)
        || Policy.normalizeEmail(loginContext.lastLoginEmail)
        || '';
}

function shouldResetRememberedEmailContext({ initialEmail, nextEmail }) {

    const normalizedInitialEmail = Policy.normalizeEmail(initialEmail);
    const normalizedNextEmail = Policy.normalizeEmail(nextEmail);

    if (!normalizedInitialEmail) {
        return false;
    }

    return normalizedInitialEmail !== normalizedNextEmail;
}

function buildRememberedEmailUpdate({ email }) {

    return {
        email
    };
}

function buildLoginContextReset({
    redirect = null,
    redirectTarget = null,
    email
}) {

    Debug.recordFlow('login_context_reset', { email });

    return {
        ...buildRememberedEmailUpdate({ email }),
        ...AuthConstants.loginContext.runtimeResetFields,
        forceSSOLoginPromptEmail: email || null,
        skipSSOLoginPromptEmail: null,
        redirect,
        redirectTarget
    };
}

function buildSignupContextReset({
    redirect = null,
    redirectTarget = null,
    email,
    schoolId = null
}) {

    Debug.recordFlow('signup_context_reset', {
        email,
        schoolId
    });

    return {
        ...buildRememberedEmailUpdate({ email }),
        schoolId,
        ...AuthConstants.signupContext.resetFields,
        agreeToTerms: AuthPersistence.getBrowserTermsAgreement(),
        forceSSOLoginPromptEmail: email || null,
        skipSSOLoginPromptEmail: null,
        redirect,
        redirectTarget
    };
}

function mergeSignupProgressContext({
    currentContext = {},
    progress = {},
    source = AuthConstants.authFlow.sources.signupPasswordCheck
} = {}) {

    const mergedContext = {
        ...currentContext,
        ...progress,
        verificationToken: progress.verificationToken || currentContext.verificationToken,
        email: progress.email || currentContext.email,
        schoolId: progress.schoolId || currentContext.schoolId,
        roleId: progress.roleId || currentContext.roleId
    };
    const authFlow = RoutingTable.resolveAuthFlow({
        currentContext,
        email: mergedContext.email,
        preserveCurrentMfa: true,
        response: mergedContext,
        source
    });

    return {
        ...mergedContext,
        ...RoutingTable.buildAuthFlowContextUpdate({ authFlow })
    };
}

function signupContextReducer({ state = {}, action, types, authTypes }) {

    if (internals.isRestoringSignupMfaChallenge({ action, authTypes, state })) {
        return state;
    }

    switch (action.type) {
        case types.CONTEXT_INIT:
            Debug.recordFlow('signup_context_init', {
                hasEmail: !!action.payload?.email,
                schoolId: action.payload?.schoolId
            });
            return action.payload || {};

        case types.CONTEXT_UPDATE:
            Debug.recordFlow('signup_context_update', {
                keys: Object.keys(action.payload || {})
            });
            return {
                ...state,
                ...action.payload
            };

        case types.SCHOOL_CHECK.REQUEST:
            return buildSignupContextReset({
                redirect: state.redirect,
                redirectTarget: state.redirectTarget,
                email: action.payload?.email || state.email,
                schoolId: action.payload?.schoolId || null
            });

        case types.SCHOOL_CHECK.SUCCESS: {
            const result = action.payload?.result || {};
            const schoolCheckFlow = RoutingTable.buildSchoolCheckFlow({
                email: action.payload?.request?.email,
                schoolCheckResult: result
            });

            Debug.recordFlow('signup_school_check_success', {
                email: action.payload?.request?.email,
                schoolId: action.payload?.request?.schoolId || schoolCheckFlow.authMode.schoolId,
                hasSSO: schoolCheckFlow.authMode.authMethod === AuthConstants.authFlow.methods.sso
            });

            return {
                ...state,
                ...action.payload?.request,
                ...result,
                ...schoolCheckFlow.signupContext,
                agreeToTerms: schoolCheckFlow.authMode.authMethod === AuthConstants.authFlow.methods.sso
                    ? false
                    : state.agreeToTerms
            };
        }

        case authTypes.LOGIN_SSO_TOKEN.SUCCESS: {
            const signupContext = action.payload?.result?.signupContext || {};
            const agreeToTerms = resolveAgreeToTerms({
                value: signupContext,
                fallback: false,
                authMethod: AuthConstants.authFlow.methods.sso
            });

            Debug.recordFlow('signup_sso_token_success', {
                email: action.payload?.request?.email,
                schoolId: action.payload?.result?.schoolId,
                agreeToTerms
            });

            return {
                ...state,
                ...signupContext,
                email: signupContext.email
                    || action.payload?.request?.email
                    || state.email,
                verificationToken: signupContext.verificationToken
                    || action.payload?.result?.verificationToken
                    || state.verificationToken,
                roleId: signupContext.roleId
                    || action.payload?.result?.roleId
                    || state.roleId,
                agreeToTerms,
                hasSSOToken: true
            };
        }

        case authTypes.LOGIN.SUCCESS: {
            const result = action.payload?.result || {};

            if (result.isVerified !== false) {
                return {};
            }

            const loginSignupContext = result.signupContext || {};

            return {
                ...loginSignupContext,
                verificationToken: loginSignupContext.verificationToken || result.verificationToken,
                schoolId: loginSignupContext.schoolId || result.schoolId,
                roleId: loginSignupContext.roleId || result.roleId,
                email: loginSignupContext.email || result.email || action.payload?.request?.email,
                isVerified: false,
                fetchedProgress: false,
                agreeToTerms: resolveAgreeToTerms({
                    value: loginSignupContext,
                    fallback: false,
                    authMethod: loginSignupContext.authMethod
                })
            };
        }

        case types.PROFILE.FETCH_SIGNUP_PROGRESS.SUCCESS:
            return {
                ...state,
                fetchedProgress: true,
                joinedSchoolIds: action.payload?.result?.joinedSchoolIds,
                agreeToTerms: resolveAgreeToTerms({
                    value: action.payload?.result,
                    fallback: state.agreeToTerms,
                    authMethod: state.authMethod
                })
            };

        case types.AUTHENTICATE.CHECK_PASSWORD.SUCCESS: {
            const data = action.payload?.result?.data || {};
            const mergedContext = mergeSignupProgressContext({
                currentContext: state,
                progress: data
            });

            return {
                ...mergedContext,
                agreeToTerms: resolveAgreeToTerms({
                    value: data,
                    fallback: state.agreeToTerms,
                    authMethod: mergedContext.authMethod
                })
            };
        }

        case types.AUTHENTICATE.COMPLETE.SUCCESS: {
            // eslint-disable-next-line no-unused-vars
            const { password, ...payload } = { ...action.payload?.request };

            return {
                ...state,
                ...payload,
                hasPassword: action.payload?.request?.password !== null,
                agreeToTerms: resolveAgreeToTerms({
                    value: payload,
                    fallback: state.agreeToTerms,
                    authMethod: state.authMethod
                })
            };
        }

        case types.AUTHENTICATE.COMPLETE_SSO.SUCCESS:
            return {
                ...state,
                ...action.payload?.request,
                agreeToTerms: resolveAgreeToTerms({
                    value: action.payload?.request,
                    fallback: state.agreeToTerms,
                    authMethod: AuthConstants.authFlow.methods.sso
                })
            };

        case types.VERIFY_EMAIL.SUCCESS:
            return {
                ...state,
                isEmailVerified: true
            };

        default:
            if (shouldClearSignupStateForAction({ action, authTypes, types })) {
                Debug.recordFlow('signup_state_cleared', {
                    actionType: action.type
                });
                return {};
            }

            return state;
    }
}

function signupIdentifyReducer({ state = {}, action, authTypes, types }) {

    switch (action.type) {
        case types.IDENTIFY.COMPLETE.SUCCESS:
            return action.payload?.request || {};

        default:
            if (shouldClearSignupStateForAction({ action, authTypes, types })) {
                return {};
            }

            return state;
    }
}

function signupAuthenticateReducer({ state = {}, action, authTypes, types }) {

    switch (action.type) {
        case types.AUTHENTICATE.COMPLETE.SUCCESS:
            return {
                ...state,
                ...action.payload?.request,
                hasPassword: action.payload?.request?.password !== null,
                agreeToTerms: resolveAgreeToTerms({
                    value: action.payload?.request,
                    fallback: state.agreeToTerms
                })
            };

        case types.AUTHENTICATE.COMPLETE_SSO.SUCCESS:
            return {
                ...state,
                ...action.payload?.request,
                agreeToTerms: resolveAgreeToTerms({
                    value: action.payload?.request,
                    fallback: state.agreeToTerms,
                    authMethod: AuthConstants.authFlow.methods.sso
                })
            };

        case types.AUTHENTICATE.CHECK_PASSWORD.SUCCESS:
            return {
                ...state,
                hasPassword: action.payload?.result?.data?.hasPassword,
                hasSSOToken: action.payload?.result?.data?.hasSSOToken
            };

        case authTypes.LOGIN_SSO_TOKEN.SUCCESS:
            return {
                ...state,
                hasSSOToken: true
            };

        default:
            if (shouldClearSignupStateForAction({ action, authTypes, types })) {
                return {};
            }

            return state;
    }
}

function signupProfileReducer({ state = {}, action, authTypes, types }) {

    switch (action.type) {
        case types.PROFILE.COMPLETE:
        case types.PROFILE.GO_BACK:
            return action.payload;

        case types.PROFILE.FETCH_SIGNUP_PROGRESS.SUCCESS:
            return {
                ...state,
                ...formatSignupProgress({
                    fetchedProgress: action.payload?.result,
                    state
                })
            };

        default:
            if (shouldClearSignupStateForAction({ action, authTypes, types })) {
                return {};
            }

            return state;
    }
}

function shouldClearSignupStateForAction({ action, authTypes, types }) {

    return [
        types.CONTEXT_INIT,
        authTypes.LOGIN.SUCCESS,
        authTypes.LOGIN_TOKEN.SUCCESS,
        authTypes.LOGOUT.SUCCESS,
        authTypes.LOGOUT_SYNC,
        authTypes.CLOSE_ACCOUNT.SUCCESS
    ].includes(action.type);
}

internals.isRestoringSignupMfaChallenge = ({ action, authTypes, state }) => action.type === authTypes.LOGIN.SUCCESS
    && !!action.payload?.request?.token
    && !!state.mfaToken;

function isInitPrerequisiteSatisfied({ signupDomain, state }) {

    return Object.keys(signupDomain.Init)
        .filter((key) => !['roleId', 'schoolId'].includes(key))
        .every((key) => !!state.context?.[key]);
}

function isSignupAuthenticationPrerequisiteSatisfied({ state }) {

    return !!state.context?.verificationToken
        && state.context?.agreeToTerms === true;
}

function isSignupPasswordPrerequisiteSatisfied({ state }) {

    return isSignupAuthenticationPrerequisiteSatisfied({ state })
        && state.context?.authMethod !== AuthConstants.authFlow.methods.sso;
}

function isSignupSSOPrerequisiteSatisfied({ state }) {

    return isSignupAuthenticationPrerequisiteSatisfied({ state })
        && state.context?.authMethod === AuthConstants.authFlow.methods.sso;
}

function getSignupContext(state = {}) {

    return state.context || {};
}

function getSignupIdentifyDetails(state = {}) {

    const identifyDetails = state[AuthConstants.stages.identify.slice] || {};
    const signupContext = state.context || {};

    return {
        ...identifyDetails,
        email: signupContext.email || identifyDetails.email,
        schoolId: signupContext.schoolId || identifyDetails.schoolId
    };
}

function getSignupAuthenticateDetails(state = {}) {

    return state[AuthConstants.stages.authenticate.slice] || {};
}

function getSignupProfileDetails(state = {}) {

    return {
        ...getSignupContext(state),
        ...getSignupAuthenticateDetails(state),
        ...(state[AuthConstants.stages.profile.slice] || {})
    };
}

function getSignupCompletionInterests(state = {}) {

    return getSignupProfileDetails(state).interests || [];
}

function getSignupCompletionPassionInterests(state = {}) {

    return getSignupProfileDetails(state).passionInterests || [];
}

function getSignupCompletionRole(state = {}) {

    return getSignupProfileDetails(state).role || {};
}

function getSignupCompletionRolePermissions(state = {}) {

    return getSignupCompletionRole(state).permissions || {};
}

function getSignupDetailsForCompletion(state = {}) {

    const profile = state[AuthConstants.stages.profile.slice] || {};

    return Object.assign({},
        state.context,
        getSignupAuthenticateDetails(state),
        profile,
        {
            type: profile.type,
            profileEmail: profile.profileEmail,
            birthdate: profile.birthdate,
            department: profile.departmentId,
            officeId: profile.officeId,
            yearHiredId: profile.yearHiredId,
            workRemote: profile.workRemote,
            major: profile.majorId ? [profile.majorId] : undefined,
            interests: JSON.stringify(Map(profile.interests, 'id')),
            passionInterests: JSON.stringify(Map(profile.passionInterests, 'id')),
            veteran: profile.veteran,
            openToSocial: profile.openToSocial,
            fullTime: profile.fullTime,
            gender: profile.gender,
            housing: stripNulls(profile.housing || {}),
            transfer: stripNulls(profile.transfer || {}),
            preferences: profile.preferences,
            hometown: profile.hometown,
            incomingClass: profile.incomingClass,
            graduatingClass: profile.graduatingClass,
            onlineStudent: profile.onlineStudent
        }
    );
}

function formatSignupProgress({ fetchedProgress, state = {} } = {}) {

    const fetchedPrefs = fetchedProgress?.preferences || {};

    if (!fetchedProgress) {
        return null;
    }

    return Object.keys(fetchedProgress).reduce((collector, key) => {

        const value = fetchedProgress[key];

        if (key === 'agreedToTerms') {
            collector.agreeToTerms = value;
            return collector;
        }

        if (PrefsDomain.prefFields.includes(key)
        || PrefsDomain.profileFields.includes(key)
        ) {

            if (!collector.preferences) {
                collector.preferences = state.preferences || fetchedPrefs;
            }

            collector.preferences[key] = value;

            return collector;
        }

        collector[key] = value;

        return collector;
    }, {});
}

function stripNulls(obj = {}) {

    return Object.keys(obj).reduce((collector, key) => {

        if (obj[key] === null) {
            return collector;
        }

        return { ...collector, [key]: obj[key] };
    }, {});
}

function resolveAgreeToTerms({ value = {}, fallback, authMethod }) {

    if (authMethod === AuthConstants.authFlow.methods.sso) {
        return fallback === true
            || value.agreeToTerms === true
            || value.agreedToTerms === true;
    }

    if (AuthPersistence.getBrowserTermsAgreement()) {
        return true;
    }

    if (typeof value.agreeToTerms === 'boolean') {
        return value.agreeToTerms;
    }

    if (typeof value.agreedToTerms === 'boolean') {
        return value.agreedToTerms;
    }

    return fallback;
}
